/**
 * TikTokPublishDialog — Audit-konformer Publish-Dialog fuer TikTok:
 * dynamische Privacy-Auswahl (live via creator_info), Interaktions-Schalter
 * (Kommentare/Duett/Stitch), Music-Usage-Einwilligung, sauberes Fehler-Handling.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, RefreshCw, Send, Music2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIVACY_LABELS = {
  PUBLIC_TO_EVERYONE: "Öffentlich (alle)",
  MUTUAL_FOLLOW_FRIENDS: "Freunde (gegenseitige Follower)",
  FOLLOWER_OF_CREATOR: "Follower",
  SELF_ONLY: "Nur ich (privat)",
};

export default function TikTokPublishDialog({ open, onClose, companyId, onConfirm, publishing }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [privacy, setPrivacy] = useState("");
  const [allowComment, setAllowComment] = useState(true);
  const [allowDuet, setAllowDuet] = useState(true);
  const [allowStitch, setAllowStitch] = useState(true);
  const [musicConsent, setMusicConsent] = useState(false);

  const loadInfo = async () => {
    setLoading(true); setError(""); setInfo(null);
    try {
      const r = await axios.get(`${API}/social/tiktok/${companyId}/creator-info`, { withCredentials: true });
      setInfo(r.data);
      setPrivacy("");  // TikTok-UX-Guideline: keine Vorauswahl — Nutzer muss aktiv waehlen
      setMusicConsent(false);
      setAllowComment(!r.data.comment_disabled);
      setAllowDuet(!r.data.duet_disabled);
      setAllowStitch(!r.data.stitch_disabled);
    } catch (e) {
      setError(e.response?.data?.detail || "Creator-Info konnte nicht geladen werden");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && companyId) loadInfo(); }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const opts = info?.privacy_level_options || [];
  const canPublish = !!info && opts.length > 0 && !!privacy && musicConsent && !publishing;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !publishing && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="tiktok-publish-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Auf TikTok veröffentlichen</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-6 flex items-center justify-center gap-2 text-sm text-[var(--studio-text-muted)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Konto-Einstellungen werden geladen…
          </div>
        )}

        {error && (
          <div className="space-y-3" data-testid="tiktok-dialog-error">
            <p className="text-sm text-red-600 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}
            </p>
            <Button variant="outline" size="sm" onClick={loadInfo} className="gap-1" data-testid="tiktok-dialog-retry">
              <RefreshCw className="w-3.5 h-3.5" /> Erneut versuchen
            </Button>
          </div>
        )}

        {info && !loading && (
          <div className="space-y-4">
            {info.creator_nickname && (
              <p className="text-xs text-[var(--studio-text-muted)]">
                Wird gepostet als <b>@{info.creator_nickname}</b>
                {info.max_video_post_duration_sec ? ` · max. Videolänge ${Math.floor(info.max_video_post_duration_sec / 60)} Min` : ""}
              </p>
            )}

            {opts.length === 0 ? (
              <p className="text-sm text-red-600" data-testid="tiktok-no-privacy-options">
                Dieses TikTok-Konto kann aktuell nicht posten (keine Privatsphäre-Optionen verfügbar).
                Bitte später erneut versuchen.
              </p>
            ) : (
              <div>
                <label className="text-xs font-medium">Wer darf dieses Video sehen?</label>
                <Select value={privacy} onValueChange={setPrivacy}>
                  <SelectTrigger className="mt-1" data-testid="tiktok-privacy-select"><SelectValue placeholder="Privatsphäre wählen" /></SelectTrigger>
                  <SelectContent>
                    {opts.map((o) => <SelectItem key={o} value={o}>{PRIVACY_LABELS[o] || o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="tiktok-sandbox-privacy-hint">
                  Hinweis: Solange die TikTok-App noch nicht das App-Review bestanden hat (Sandbox),
                  akzeptiert TikTok nur <b>„Nur ich (privat)"</b>. Bei anderer Auswahl wird automatisch
                  auf „Nur ich" zurückgestuft.
                </p>
              </div>
            )}

            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm">Kommentare erlauben {info.comment_disabled && <span className="text-[10px] text-slate-400">(vom Konto deaktiviert)</span>}</span>
                <Switch checked={allowComment} onCheckedChange={setAllowComment} disabled={info.comment_disabled} data-testid="tiktok-comment-switch" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Duett erlauben {info.duet_disabled && <span className="text-[10px] text-slate-400">(vom Konto deaktiviert)</span>}</span>
                <Switch checked={allowDuet} onCheckedChange={setAllowDuet} disabled={info.duet_disabled} data-testid="tiktok-duet-switch" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Stitch erlauben {info.stitch_disabled && <span className="text-[10px] text-slate-400">(vom Konto deaktiviert)</span>}</span>
                <Switch checked={allowStitch} onCheckedChange={setAllowStitch} disabled={info.stitch_disabled} data-testid="tiktok-stitch-switch" />
              </div>
            </div>

            <label className="flex items-start gap-2 text-[11px] text-[var(--studio-text-muted)] border border-[var(--studio-border)] rounded-lg p-2.5 cursor-pointer" data-testid="tiktok-music-consent">
              <input type="checkbox" checked={musicConsent} onChange={(e) => setMusicConsent(e.target.checked)}
                className="mt-0.5 accent-[#2563EB]" data-testid="tiktok-music-consent-checkbox" />
              <span className="flex items-start gap-1.5">
                <Music2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>
                  Ich bestätige TikToks{" "}
                  <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer" className="underline">
                    Music Usage Confirmation
                  </a>{" "}
                  und dass ich über alle Rechte am Video verfüge.
                </span>
              </span>
            </label>

            <Button
              className="w-full gap-1.5"
              disabled={!canPublish}
              onClick={() => onConfirm({
                privacy_level: privacy,
                disable_comment: !allowComment,
                disable_duet: !allowDuet,
                disable_stitch: !allowStitch,
              })}
              data-testid="tiktok-publish-confirm"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Jetzt auf TikTok veröffentlichen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
